/**
 * Display names for calibre / calibre-image `source` values.
 *
 * The stored values are slugs (`user_submitted`, `watchkeeper`) and were being
 * rendered raw in the calibre editor's two source dropdowns, so a picker read
 * "watchkeeper" where the product says "Watch Keeper". One map, used by every
 * surface that shows a source, so the admin and the app cannot disagree about
 * what a value is called.
 *
 * Imports the type from `calibreSources`, not `firebase`, so this module drags
 * in nothing at runtime and can be used from anywhere.
 */

import type { CalibreImageSource } from './calibreSources';

/**
 * Keyed by the full image vocabulary, so adding a source without a label is a
 * type error rather than a blank option. The entity vocabulary is a subset, so
 * this covers both pickers.
 */
export const CALIBRE_SOURCE_LABELS: Record<CalibreImageSource, string> = {
  manual: 'Manual',
  brand: 'Brand',
  manufacturer: 'Manufacturer',
  calibrecorner: 'CalibreCorner',
  user_submitted: 'User submitted',
  // Two words, matching the product name and the RN attribution label.
  watchkeeper: 'Watch Keeper',
};

/**
 * A source's display name, falling back to the raw value.
 *
 * The fallback is load-bearing: the manual pickers append whatever value an
 * entry already carries, which may be a historic tag (`custom`) that no longer
 * appears in any vocabulary. Showing it verbatim is right — the point of
 * appending it is that the admin can see what is stored before deciding to
 * change it.
 */
export function labelForCalibreSource(value: string): string {
  return CALIBRE_SOURCE_LABELS[value as CalibreImageSource] ?? value;
}
